import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import {
  createRole,
  fetchRole,
  isSystemRoleName,
  listRoles,
  syncRolesFromApi,
  updateRole,
  type RoleDraft,
  type RoleRecord,
} from "@/lib/admin/rolesStore";
import {
  listManagedPermissions,
  syncManagedPermissionsFromApi,
  type ManagedPermissionRecord,
} from "@/lib/admin/managedPermissionsStore";
import { fetchCompanyOwnerRolePermissions } from "@/lib/admin/companiesStore";
import { can, getStoredAuthUser, isSuperAdmin } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  CalendarIcon,
  CheckCircle2,
  Coffee,
  Edit,
  Filter,
  KeyRound,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Search,
  Shield,
  XCircle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type RoleSortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "updated_latest" | "updated_oldest";

function roleErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

function sortPermissionCatalog(items: ManagedPermissionRecord[]): ManagedPermissionRecord[] {
  return [...items].sort((a, b) => {
    const ca = (a.categoryName ?? "").localeCompare(b.categoryName ?? "");
    if (ca !== 0) return ca;
    return a.title.localeCompare(b.title) || a.name.localeCompare(b.name);
  });
}

function matchesPermissionSearch(p: ManagedPermissionRecord, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return (
    p.name.toLowerCase().includes(n) ||
    p.title.toLowerCase().includes(n) ||
    (p.categoryName && p.categoryName.toLowerCase().includes(n))
  );
}

function groupPermsByCategory(items: ManagedPermissionRecord[]): Array<{ category: string; items: ManagedPermissionRecord[] }> {
  const by = new Map<string, ManagedPermissionRecord[]>();
  for (const p of items) {
    const key = (p.categoryName ?? "").trim() || "Uncategorized";
    const arr = by.get(key) ?? [];
    arr.push(p);
    by.set(key, arr);
  }
  const categories = Array.from(by.keys()).sort((a, b) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    return a.localeCompare(b);
  });
  return categories.map((category) => ({ category, items: by.get(category) ?? [] }));
}

function checkedState(selectedCount: number, totalCount: number): boolean | "indeterminate" {
  if (totalCount <= 0) return false;
  if (selectedCount <= 0) return false;
  if (selectedCount >= totalCount) return true;
  return "indeterminate";
}

function roleScopeLabel(r: RoleRecord): string {
  if (r.isTemplate) return "Template role";
  if (r.companyName && r.companyName.trim()) return `${r.companyName} role`;
  if (!r.companyId) return "Template role";
  return "Company role";
}

function roleCardTitle(r: RoleRecord): string {
  // Source of truth: global role rows (companyId null) are always "Default".
  if (!r.companyId || r.isTemplate) return "Default";
  if (r.companyName && r.companyName.trim()) return r.companyName;
  return "Company";
}

const emptyDraft = (): RoleDraft => ({
  name: "",
  permissionNames: [],
});

export default function Roles() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "role_can_view");
  const canCreate = can(user, "role_can_create");
  const canUpdate = can(user, "role_can_update");
  const showRoleTypeColumn = isSuperAdmin(user);
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<RoleSortBy>("name_asc");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");

  const [editing, setEditing] = useState<RoleRecord | null>(null);
  const [draft, setDraft] = useState<RoleDraft>(emptyDraft());
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [permissionCatalog, setPermissionCatalog] = useState<ManagedPermissionRecord[]>([]);
  const [selectedPermissionNames, setSelectedPermissionNames] = useState<Set<string>>(new Set());
  const [availablePermsQuery, setAvailablePermsQuery] = useState("");
  const [selectedPermsQuery, setSelectedPermsQuery] = useState("");
  const [permFeedback, setPermFeedback] = useState<{ type: "success" | "error"; message: string; id: number } | null>(null);

  const roles = useMemo(() => listRoles(), [version]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsBootstrapping(true);
      setIsForbidden(false);
      try {
        await syncRolesFromApi();
        if (mounted) refresh();
      } catch (error) {
        console.error("[Roles] Load failed", error);
        if (isAxiosError(error) && error.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        if (mounted) toast.error(roleErrorMessage(error));
      } finally {
        if (mounted) setIsBootstrapping(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();

    return roles.filter((r) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(r.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(r.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (nameNeedle && !r.name.toLowerCase().includes(nameNeedle)) return false;
      return !q || r.name.toLowerCase().includes(q);
    });
  }, [roles, query, filterCreatedDate, quickDate, filterName]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = (filterCreatedDate ? 1 : 0) + (filterName.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const closeRoleEditor = () => {
    setRoleEditorOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
    setPermissionCatalog([]);
    setSelectedPermissionNames(new Set());
    setAvailablePermsQuery("");
    setSelectedPermsQuery("");
    setCatalogLoading(false);
    setPermFeedback(null);
  };

  const togglePermission = (name: string, nextChecked: boolean, title: string) => {
    setSelectedPermissionNames((prev) => {
      const n = new Set(prev);
      if (nextChecked) n.add(name);
      else n.delete(name);
      return n;
    });
    window.setTimeout(() => {
      setPermFeedback({
        type: nextChecked ? "success" : "error",
        message: nextChecked ? `“${title}” was added to this role.` : `“${title}” was removed from this role.`,
        id: Date.now(),
      });
    }, 0);
  };

  useEffect(() => {
    if (!permFeedback) return;
    const t = window.setTimeout(() => setPermFeedback(null), 4200);
    return () => window.clearTimeout(t);
  }, [permFeedback]);

  const isCompanyOwnerAccount = useCallback(() => {
    const u = getStoredAuthUser();
    const role = (u?.role ?? "").toLowerCase().trim();
    return role === "company-owner" || role === "company owner";
  }, []);

  const applyCompanyOwnerPermissionScope = useCallback(
    async (catalog: ManagedPermissionRecord[]): Promise<ManagedPermissionRecord[]> => {
      if (!isCompanyOwnerAccount()) return catalog;
      const u = getStoredAuthUser();
      // Company-owner users may not have `company_can_view`, so calling the company
      // owner-role permissions endpoint can 403. Prefer the already-computed effective
      // permissions from `auth_user`.
      const allowedFromUser = Array.isArray(u?.permissions) ? u!.permissions : [];
      if (allowedFromUser.length > 0) {
        const allowed = new Set(allowedFromUser);
        return catalog.filter((p) => allowed.has(p.name));
      }

      // Fallback (best-effort) for older sessions where `auth_user.permissions` is empty.
      const companyId = u?.company_id;
      if (!companyId || companyId < 1) return [];
      try {
        const preview = await fetchCompanyOwnerRolePermissions(String(companyId));
        const allowed = new Set(preview.permissionNames);
        return catalog.filter((p) => allowed.has(p.name));
      } catch {
        return [];
      }
    },
    [isCompanyOwnerAccount],
  );

  const openCreate = () => {
    if (!canCreate) return;
    setEditing(null);
    setDraft(emptyDraft());
    setSelectedPermissionNames(new Set());
    setAvailablePermsQuery("");
    setSelectedPermsQuery("");
    setPermFeedback(null);
    setRoleEditorOpen(true);
    void (async () => {
      setCatalogLoading(true);
      try {
        await syncManagedPermissionsFromApi();
        const full = sortPermissionCatalog(listManagedPermissions());
        const scoped = await applyCompanyOwnerPermissionScope(full);
        setPermissionCatalog(scoped);
      } catch (error) {
        console.error("[Roles] Permission catalog failed", error);
        toast.error(roleErrorMessage(error));
        closeRoleEditor();
      } finally {
        setCatalogLoading(false);
      }
    })();
  };

  const openEdit = (r: RoleRecord) => {
    if (!canView) return;
    setEditing(r);
    setDraft({ name: r.name, permissionNames: [] });
    setSelectedPermissionNames(new Set());
    setAvailablePermsQuery("");
    setSelectedPermsQuery("");
    setPermFeedback(null);
    setRoleEditorOpen(true);
    void (async () => {
      setCatalogLoading(true);
      try {
        const [detail] = await Promise.all([fetchRole(r.id), syncManagedPermissionsFromApi()]);
        const full = sortPermissionCatalog(listManagedPermissions());
        const scoped = await applyCompanyOwnerPermissionScope(full);
        setPermissionCatalog(scoped);
        setDraft({ name: detail.name, permissionNames: detail.permissionNames });
        setSelectedPermissionNames(new Set(detail.permissionNames));
      } catch (error) {
        console.error("[Roles] Load role / catalog failed", error);
        toast.error(roleErrorMessage(error));
        closeRoleEditor();
      } finally {
        setCatalogLoading(false);
      }
    })();
  };

  const availablePermissionRows = useMemo(
    () => permissionCatalog.filter((p) => !selectedPermissionNames.has(p.name)),
    [permissionCatalog, selectedPermissionNames],
  );
  const selectedPermissionRows = useMemo(
    () => permissionCatalog.filter((p) => selectedPermissionNames.has(p.name)),
    [permissionCatalog, selectedPermissionNames],
  );

  const filteredAvailablePerms = useMemo(
    () => availablePermissionRows.filter((p) => matchesPermissionSearch(p, availablePermsQuery)),
    [availablePermissionRows, availablePermsQuery],
  );
  const filteredSelectedPerms = useMemo(
    () => selectedPermissionRows.filter((p) => matchesPermissionSearch(p, selectedPermsQuery)),
    [selectedPermissionRows, selectedPermsQuery],
  );

  const groupedAvailablePerms = useMemo(() => groupPermsByCategory(filteredAvailablePerms), [filteredAvailablePerms]);
  const groupedSelectedPerms = useMemo(() => groupPermsByCategory(filteredSelectedPerms), [filteredSelectedPerms]);

  const categoryToAllPermissionNames = useMemo(() => {
    const by = new Map<string, string[]>();
    for (const p of permissionCatalog) {
      const key = (p.categoryName ?? "").trim() || "Uncategorized";
      const arr = by.get(key) ?? [];
      arr.push(p.name);
      by.set(key, arr);
    }
    return by;
  }, [permissionCatalog]);

  const togglePermissionCategory = (category: string, side: "available" | "selected") => {
    const names = categoryToAllPermissionNames.get(category) ?? [];
    if (names.length === 0) return;
    // Radix tri-state clicks are unreliable; derive intent from the list you clicked in:
    // - Available: none selected in category → add all; any selected → clear category (matches “pick from left”)
    // - Selected: not all selected → add missing (restore); all selected → clear category (matches “manage on right”)
    const addAll =
      side === "available"
        ? !names.some((n) => selectedPermissionNames.has(n))
        : !names.every((n) => selectedPermissionNames.has(n));
    setSelectedPermissionNames((prev) => {
      const next = new Set(prev);
      if (addAll) {
        for (const n of names) next.add(n);
      } else {
        for (const n of names) next.delete(n);
      }
      return next;
    });

    window.setTimeout(() => {
      setPermFeedback({
        type: addAll ? "success" : "error",
        message: addAll
          ? `“${category}” permissions were added to this role.`
          : `“${category}” permissions were removed from this role.`,
        id: Date.now(),
      });
    }, 0);
  };

  const toggleAllPermissions = (side: "available" | "selected") => {
    const isAdding = side === "available";
    const names = permissionCatalog.map((p) => p.name);

    setSelectedPermissionNames((prev) => {
      if (isAdding) {
        const next = new Set(prev);
        for (const n of names) next.add(n);
        return next;
      }
      return new Set();
    });

    window.setTimeout(() => {
      setPermFeedback({
        type: isAdding ? "success" : "error",
        message: isAdding ? "All permissions were added to this role." : "All permissions were removed from this role.",
        id: Date.now(),
      });
    }, 0);
  };

  const saveRoleEditor = async () => {
    if (isSaving) return;
    if (!draft.name.trim()) {
      toast.error("Role name is required.");
      return;
    }
    const payload: RoleDraft = {
      name: draft.name.trim(),
      permissionNames: Array.from(selectedPermissionNames).sort(),
    };

    setIsSaving(true);
    try {
      if (editing) {
        if (isSystemRoleName(editing.name) && payload.name !== editing.name) {
          toast.error("System roles cannot be renamed.");
          return;
        }
        const result = await updateRole(editing.id, payload);
        if (!result) {
          toast.error("Role not found.");
          return;
        }
        toast.success(result.message);
      } else {
        const result = await createRole(payload);
        toast.success(result.message);
      }
      closeRoleEditor();
      refresh();
    } catch (error) {
      console.error("[Roles] Save failed", error);
      toast.error(roleErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const editingIsSystem = editing ? isSystemRoleName(editing.name) : false;
  const permTogglesDisabled = editing ? !canUpdate : !canCreate;

  const renderAvailablePermRow = (p: ManagedPermissionRecord) => (
    <div key={p.name} className="flex items-start gap-2 rounded-md border border-border/50 bg-background/80 p-2">
      <Checkbox
        checked={selectedPermissionNames.has(p.name)}
        onCheckedChange={(c) => togglePermission(p.name, c === true, p.title)}
        className="mt-0.5"
        id={`perm-av-${p.name}`}
        disabled={permTogglesDisabled}
      />
      <div className="min-w-0 flex-1">
        <Label htmlFor={`perm-av-${p.name}`} className="text-xs font-medium text-foreground cursor-pointer leading-snug line-clamp-2">
          {p.title}
        </Label>
      </div>
    </div>
  );

  const renderSelectedPermRow = (p: ManagedPermissionRecord) => (
    <div key={p.name} className="flex items-start gap-2 rounded-md border border-border/50 bg-background/80 p-2">
      <Checkbox
        checked={selectedPermissionNames.has(p.name)}
        onCheckedChange={(c) => togglePermission(p.name, c === true, p.title)}
        className="mt-0.5"
        id={`perm-sel-${p.name}`}
        disabled={permTogglesDisabled}
      />
      <div className="min-w-0 flex-1">
        <Label htmlFor={`perm-sel-${p.name}`} className="text-xs font-medium text-foreground cursor-pointer leading-snug">
          {p.title}
        </Label>
      </div>
    </div>
  );

  const content = (
    <Layout title="Roles">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Roles</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Create roles and assign Spatie permissions (same layout as work policies).{" "}
              <Link to="/permissions" className="text-primary hover:underline font-medium">
                Permissions
              </Link>
              {" · "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          <Button variant="premium" className="gap-2" onClick={openCreate} disabled={!canCreate}>
            <Plus className="w-4 h-4" />
            Add role
          </Button>
        </div>

        <Dialog
          open={roleEditorOpen}
          onOpenChange={(o) => {
            if (!o) closeRoleEditor();
            else setRoleEditorOpen(true);
          }}
        >
          <DialogContent className="!flex min-h-0 max-w-4xl w-[96vw] max-h-[90vh] flex-col gap-0 p-0 overflow-hidden sm:max-h-[90vh]">
            <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
              <DialogTitle className="text-left">
                {editing ? "Edit role" : "New role"}
                {draft.name.trim() || editing?.name ? (
                  <span className="block text-sm font-normal text-muted-foreground mt-1">{draft.name.trim() || editing?.name}</span>
                ) : null}
              </DialogTitle>
            </DialogHeader>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-1 pb-3 [-webkit-overflow-scrolling:touch]">
                <div className="space-y-3">
                  <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-border/60 bg-muted/15 p-3 shadow-sm">
                    <Label className="text-xs text-muted-foreground">
                      Role name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      className={cn(
                        "glass-input h-10",
                        editingIsSystem && "bg-muted/40 cursor-default",
                      )}
                      value={draft.name}
                      onChange={(e) => {
                        setDraft((d) => ({ ...d, name: e.target.value }));
                      }}
                      placeholder="e.g. Store Manager"
                      maxLength={100}
                      disabled={editingIsSystem || (!!editing && !canUpdate) || (!editing && !canCreate)}
                      readOnly={editingIsSystem || (!!editing && !canUpdate) || (!editing && !canCreate)}
                      aria-readonly={editingIsSystem || (!!editing && !canUpdate) || (!editing && !canCreate) || undefined}
                    />
                    {editingIsSystem ? (
                      <p className="text-xs text-muted-foreground">System roles cannot be renamed. You can still update permissions.</p>
                    ) : null}
                  </div>

                  {!catalogLoading ? (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        Use the left list to add permissions; manage assigned permissions on the right. Search filters each column.
                      </p>
                      <div className="relative">
                        <AnimatePresence mode="wait">
                          {permFeedback ? (
                            <motion.div
                              key={permFeedback.id}
                              role="status"
                              initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
                              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                              exit={{ opacity: 0, y: -8, filter: "blur(4px)" }}
                              transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.6 }}
                              className={cn(
                                "flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm shadow-sm backdrop-blur-[2px]",
                                permFeedback.type === "success"
                                  ? "border-emerald-500/40 bg-emerald-500/[0.12] text-emerald-950 dark:border-emerald-400/35 dark:bg-emerald-500/15 dark:text-emerald-50"
                                  : "border-red-500/45 bg-red-500/[0.12] text-red-950 dark:border-red-400/40 dark:bg-red-500/15 dark:text-red-50",
                              )}
                            >
                              {permFeedback.type === "success" ? (
                                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                              ) : (
                                <XCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-600 dark:text-red-400" aria-hidden />
                              )}
                              <span className="leading-snug">{permFeedback.message}</span>
                            </motion.div>
                          ) : null}
                        </AnimatePresence>
                      </div>
                    </div>
                  ) : null}

                  {catalogLoading ? (
                    <div className="flex justify-center py-16 text-muted-foreground">
                      <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                      <div className="flex min-h-0 flex-col rounded-xl border border-border/60 bg-muted/15 p-3 gap-2 shadow-sm">
                        <div className="flex items-center justify-between gap-2 shrink-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-foreground">Available</h3>
                            {availablePermissionRows.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-1.5 text-[10px] text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                                onClick={() => toggleAllPermissions("available")}
                                disabled={permTogglesDisabled}
                              >
                                Select All
                              </Button>
                            )}
                          </div>
                          <span className="text-[11px] text-muted-foreground tabular-nums">
                            {filteredAvailablePerms.length}/{availablePermissionRows.length}
                          </span>
                        </div>
                        <div className="relative shrink-0">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                          <Input
                            className="glass-input h-8 pl-8 text-xs"
                            placeholder="Search available…"
                            value={availablePermsQuery}
                            onChange={(e) => setAvailablePermsQuery(e.target.value)}
                            autoComplete="off"
                          />
                        </div>
                        <div className="min-h-[120px] max-h-[min(48vh,420px)] overflow-y-auto overscroll-contain space-y-1.5 pr-0.5 [-webkit-overflow-scrolling:touch]">
                          {permissionCatalog.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">
                              No permissions in the catalog. Define permissions under{" "}
                              <Link to="/permissions" className="text-primary underline">
                                Permissions
                              </Link>
                              .
                            </p>
                          ) : availablePermissionRows.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">All permissions are assigned.</p>
                          ) : filteredAvailablePerms.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">No matches.</p>
                          ) : (
                            groupedAvailablePerms.map((g) => (
                              <div key={g.category} className="space-y-1.5">
                                <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-background/90 backdrop-blur border-b border-border/40">
                                  <div className="flex items-center gap-2">
                                    <Checkbox
                                      id={`perm-cat-av-${g.category}`}
                                      checked={checkedState(
                                        (categoryToAllPermissionNames.get(g.category) ?? []).filter((n) => selectedPermissionNames.has(n)).length,
                                        (categoryToAllPermissionNames.get(g.category) ?? []).length,
                                      )}
                                      onCheckedChange={() => togglePermissionCategory(g.category, "available")}
                                      disabled={permTogglesDisabled}
                                    />
                                    <Label
                                      htmlFor={`perm-cat-av-${g.category}`}
                                      className="text-[11px] font-semibold text-muted-foreground cursor-pointer select-none"
                                    >
                                      {g.category}
                                    </Label>
                                  </div>
                                </div>
                                <div className="pl-4 space-y-1.5">{g.items.map((p) => renderAvailablePermRow(p))}</div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      <div className="flex min-h-0 flex-col rounded-xl border border-border/60 bg-muted/15 p-3 gap-2 shadow-sm">
                        <div className="flex items-center justify-between gap-2 shrink-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-foreground">Selected</h3>
                            {selectedPermissionRows.length > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                                onClick={() => toggleAllPermissions("selected")}
                                disabled={permTogglesDisabled}
                              >
                                Deselect All
                              </Button>
                            )}
                          </div>
                          <span className="text-[11px] text-muted-foreground tabular-nums">
                            {filteredSelectedPerms.length}/{selectedPermissionRows.length}
                          </span>
                        </div>
                        <div className="relative shrink-0">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                          <Input
                            className="glass-input h-8 pl-8 text-xs"
                            placeholder="Search selected…"
                            value={selectedPermsQuery}
                            onChange={(e) => setSelectedPermsQuery(e.target.value)}
                            autoComplete="off"
                          />
                        </div>
                        <div className="min-h-[120px] max-h-[min(48vh,420px)] overflow-y-auto overscroll-contain space-y-1.5 pr-0.5 [-webkit-overflow-scrolling:touch]">
                          {selectedPermissionRows.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">Check permissions on the left to assign them.</p>
                          ) : filteredSelectedPerms.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">No matches.</p>
                          ) : (
                            groupedSelectedPerms.map((g) => (
                              <div key={g.category} className="space-y-1.5">
                                <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-background/90 backdrop-blur border-b border-border/40">
                                  <div className="flex items-center gap-2">
                                    <Checkbox
                                      id={`perm-cat-sel-${g.category}`}
                                      checked={checkedState(
                                        (categoryToAllPermissionNames.get(g.category) ?? []).filter((n) => selectedPermissionNames.has(n)).length,
                                        (categoryToAllPermissionNames.get(g.category) ?? []).length,
                                      )}
                                      onCheckedChange={() => togglePermissionCategory(g.category, "selected")}
                                      disabled={permTogglesDisabled}
                                    />
                                    <Label
                                      htmlFor={`perm-cat-sel-${g.category}`}
                                      className="text-[11px] font-semibold text-muted-foreground cursor-pointer select-none"
                                    >
                                      {g.category}
                                    </Label>
                                  </div>
                                </div>
                                <div className="pl-4 space-y-1.5">{g.items.map((p) => renderSelectedPermRow(p))}</div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="px-6 py-4 border-t border-border/60 shrink-0 bg-muted/10">
              <Button type="button" variant="glass" onClick={closeRoleEditor} disabled={isSaving}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="premium"
                onClick={() => void saveRoleEditor()}
                disabled={isSaving || catalogLoading || (editing ? !canUpdate : !canCreate)}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">{appliedFiltersCount}</span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter roles</SheetTitle>
                  <SheetDescription>Name and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Role</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input className="glass-input h-10" value={filterName} onChange={(e) => setFilterName(e.target.value)} autoComplete="off" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetSheetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="roles-page-size-mobile" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as RoleSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="updated_latest">Recently updated</SelectItem>
                  <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="roles-page-size-desktop" pageSize={pageSize} onPageSizeChange={setPageSize} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as RoleSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="updated_latest">Recently updated</SelectItem>
                    <SelectItem value="updated_oldest">Least recently updated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isBootstrapping ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading roles…
          </div>
        ) : null}

        {!isBootstrapping && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {roles.length === 0 ? "No roles yet. Click “Add role” to create one." : "No roles match your search."}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CatalogEntityCard
                  title={r.name}
                  metaColumns={1}
                  primaryMeta={{
                    label: "Permissions",
                    value: String(r.permissionsCount),
                    icon: KeyRound,
                    iconClassName: "text-violet-600",
                  }}
                  secondaryMeta={
                    showRoleTypeColumn
                      ? {
                          label: "Role type",
                          value: r.isTemplate ? "Template role" : roleScopeLabel(r),
                          icon: Coffee,
                          iconClassName: "text-sky-600",
                        }
                      : undefined
                  }
                  onEdit={canView ? () => openEdit(r) : undefined}
                  onDelete={undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="glass-card p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{r.name}</h3>
                    <div className="mt-0.5">
                      <p className="text-xs text-muted-foreground truncate">
                        {r.permissionsCount} permission{r.permissionsCount === 1 ? "" : "s"}
                      </p>
                      {showRoleTypeColumn ? (
                        <p className="text-xs text-muted-foreground truncate">{r.isTemplate ? "Template role" : roleScopeLabel(r)}</p>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Updated {formatCatalogAuditDate(r.updatedAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(r)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!isBootstrapping && sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>
    </Layout>
  );

  return isForbidden ? (
    <PermissionDenied
      title="Roles access required"
      message="You don’t have permission to view roles. Ask an administrator to grant role_can_view_any."
    />
  ) : (
    content
  );
}
